import axios from "axios";
import _ from "lodash";
import { CANCEL } from "redux-saga";
import { modeMedian } from "d3fc-sample";
import moment from "moment";
import "moment-duration-format";

const CancelToken = axios.CancelToken;
let source;

// Import store to get access to state data - we'll use this to decide whether to fetch new POI data from the API
import store from "../store";

// Route colours
const trackColour = "#A6804E";
const minorRoadColour = "#1E90FF";
const majorRoadColour = "#00008B";
const pedestrianAreaColour = "#BF38D3";
const otherColour = "#6F6F6E";
const dismountedRoadColour = "#ff3333";

// API request URL. We append specific queries to this in getjourneys()
const API_ROOT =
  "https://www.cyclestreets.net/api/journey.json?key=8db8956117439aa4&itinerarypoints=";

// API request function
function callCycleStreets(query_url, queryType) {
  switch (queryType) {
    case "poi":
      break;

    default:
      query_url = API_ROOT + query_url;
  }

  return new Promise((resolve, reject) => {
    axios
      .get(query_url, {
        cancelToken: source.token,
        timeout: 10000,
      })
      .then((response) => {
        // Check returned data
        if (response.data) {
          switch (queryType) {
            case "poi":
              resolve(response.data.features);
              break;

            default:
              resolve(response.data);
          }
        } else {
          reject("no posts");
        }
      })
      .catch((error) => {
        // Reject all but cancelled (as just discarded)
        if (!axios.isCancel(error)) {
          reject(error);
        }
      });
  });
}

// Function to prepare API request, and process the returned data
export const getjourneys = (options) => {
  // Empty variable that we'll update with lat and lng coordinates for each desired waypoint
  let waypointsString = "";

  options.locations.forEach((location) => {
    // If waypointsString is empty, just use the current location lat and lng values
    if (waypointsString === "") {
      waypointsString = `${location.lng},${location.lat}`;
    }
    // Else, add the current location lat and lng values to the existing string as a new coordinate pair
    else {
      waypointsString = `${waypointsString}|${location.lng},${location.lat}`;
    }
  });

  const queryType = "planning";

  // Set up query URLs to append to API_ROOT defined above
  const fastest_query_url = `${waypointsString}&plan=fastest`;
  const balanced_query_url = `${waypointsString}&plan=balanced`;
  const quietest_query_url = `${waypointsString}&plan=quietest`;

  // Call the callCycleStreets() function above to get the data
  const promise = new Promise((resolve, reject) => {
    source = CancelToken.source();

    Promise
      // Submit the 3 queries to the API
      .all([
        callCycleStreets(fastest_query_url, queryType),
        callCycleStreets(balanced_query_url, queryType),
        callCycleStreets(quietest_query_url, queryType),
      ])
      // When all 3 have returned, prepare a new object containing the data
      .then((response) => {
        let elevationMax = 0; // Used for Y-axis scale

        // For each journey type (balanced, quietest, fastest), we process the API data and format it for output in components
        Object.keys(response).forEach((i) => {
          // Store the current journey type
          const journey = response[i];

          /*
					============================
					* Part 1 - Journey Summary *
					============================
					*/

          // Create a new object to store processed journey summary data - distance, duration, ascent and descent values
          journey.summary = {};

          // Get the journey data (first item of marker array returned from API)
          const journeyData = journey.marker[0]["@attributes"];

          // Set distance of journey (miles)
          journey.summary.distance = parseFloat(
            journeyData.length * 0.000621371
          ).toFixed(1);

          // Set duration of journey
          const duration = parseInt(journeyData.time, 10);
          journey.summary.duration = {
            value: duration,
            text: moment.duration(duration, "seconds").format("h[hr] m[min]"),
          };

          // Set up defaults for the total ascent and descent values of journey (m)
          journey.summary.ascent = 0;
          journey.summary.descent = 0;

          // Create a new array to store elevation values as integers, not the one large string returned by the API
          const elevations = journeyData.elevations
            .split(",")
            .map((elevation) => parseFloat(elevation));
          const distances = journeyData.distances
            .split(",")
            .map((distance) => parseFloat(distance));
          const coordinates = journeyData.coordinates
            .split(" ")
            .map((coordinate) => {
              const location = coordinate.split(",");

              return {
                lat: parseFloat(location[1]),
                lng: parseFloat(location[0]),
              };
            });
          distances.unshift(0); // add 0 to first distance to math array lengths

          /*
					Loop through the array of integers, adding the difference between the current elevation
					and the previous one to either ascent or descent depending on whether the value is positive
					or negative
					*/
          elevations.forEach((current, index) => {
            const previous = elevations[index === 0 ? index : index - 1];

            if (current - previous > 0) {
              journey.summary.ascent += current - previous;
            } else {
              journey.summary.descent += (current - previous) * -1;
            }
          });

          /*
					==========================
					* Part 2 - Route Profile *
					==========================
					*/

          // Create an array to store the route profile data
          journey.routeProfile = [];

          let provisions = [];

          // Create an array to store unique provisions (road types) along the current route
          let uniques = [];

          let dismountDistance = 0; // Used to store the total distance of dismounted sections of the route

          // Set up an array of all the provisions for the route (shifted to remove initial "undefined"):
          journey.marker.forEach((provision) => {
            provisions.push({
              name: provision["@attributes"].provisionName,
              distance: provision["@attributes"].distance,
              dismount: provision["@attributes"].walk === "1" ? true : false,
            });
          });
          provisions.shift();

          // Loop through the provisions array
          provisions.forEach((provision) => {
            // Use lodash to get a reference to the provision if it is already stored in the uniques array
            const stored = _.findKey(uniques, { name: provision.name });

            // If the provision is not found in the uniques array, add it as a new object and set its distance
            if (!uniques[stored]) {
              const obj = {};
              obj.name = provision.name;
              obj.distance = parseInt(provision.distance, 10);
              obj.dismount = provision.dismount;

              uniques.push(obj);

              // If it is already in the uniques array, increase its distance
            } else {
              uniques[stored].distance += parseInt(provision.distance, 10);
            }

            if (provision.dismount) {
              // If the provision is a dismounted section, add its distance to the total dismount distance
              dismountDistance += parseInt(provision.distance, 10);
            }
          });

          // Store the percentage of the overall journey distance that each unique provision represents
          uniques.forEach(
            (prov) =>
              (prov.percentage = Math.round(
                (prov.distance / journeyData.length) * 100
              ))
          );

          // Calculate the dismount percentage based on total journey distance
          const dismountPercentage = Math.round(
            (dismountDistance / journeyData.length) * 100
          );

          /* Next we check the current provision name against a list of fairly arbitrary categories
					we've chosen to be output in the RouteProfile: Major, Minor, Track, Pedestrianised, Other.

					Example 1: Both "main" and "major" road provisions returned by the API should be grouped
					together as "Major roads" in our route profile, with their percentage values added together.

					Example 2: "Minor", "trunk", "service", and "residential" roads should be categorised as
					"Minor road" in our route profile, with their percentage values added together.

					Another category for pedestrianised areas covers several provision types, and anything else
					returned by the API is assigned to "Other" in the route profile. */

          // Loop through the uniques array, combining provisions in to simplified categories as detailed above
          uniques.forEach((prov) => {
            // Use lodash to get references to the categories if they already exist in the routeProfile array for this journey
            const major = _.findKey(journey.routeProfile, {
              name: "Major road",
            });
            const minor = _.findKey(journey.routeProfile, {
              name: "Minor road",
            });
            const track = _.findKey(journey.routeProfile, { name: "Track" });
            const other = _.findKey(journey.routeProfile, { name: "Other" });
            const pedestrian = _.findKey(journey.routeProfile, {
              name: "Pedestrian area",
            });
            const dismounted = _.findKey(journey.routeProfile, {
              name: "Dismount",
            });

            // Group provisions in to categories
            switch (prov.name) {
              // "Major"
              case "Major road":
              case "Main road":
              case "Trunk road":
                /* If "Major road" is not already a category, we create it and use the provision's
								percentage as its percentage value */
                if (!journey.routeProfile[major]) {
                  const obj = {};
                  obj.name = "Major road";
                  obj.percentage = prov.percentage;
                  obj.color = majorRoadColour;

                  journey.routeProfile.push(obj);
                }
                // Else, we add the current provision's percentage to the category's existing one
                else {
                  journey.routeProfile[major].percentage += prov.percentage;
                }

                break;

              // "Minor"
              case "Minor road":
              case "Service road":
              case "Trunk road access":
              case "Residential street":
              case "Unclassified road":
                if (!journey.routeProfile[minor]) {
                  const obj = {};
                  obj.name = "Minor road";
                  obj.percentage = prov.percentage;
                  obj.color = minorRoadColour;

                  journey.routeProfile.push(obj);
                } else {
                  journey.routeProfile[minor].percentage += prov.percentage;
                }

                break;

              // "Pedestrian"
              case "Footpath":
              case "Pedestrianised area":
              case "Path":
                if (!journey.routeProfile[pedestrian]) {
                  const obj = {};
                  obj.name = "Pedestrian area";
                  obj.percentage = prov.percentage;
                  obj.color = pedestrianAreaColour;

                  journey.routeProfile.push(obj);
                } else {
                  journey.routeProfile[pedestrian].percentage +=
                    prov.percentage;
                }

                break;

              // "Track"
              case "Cycle path":
              case "Track":
              case "Bridleway":
                if (!journey.routeProfile[track]) {
                  const obj = {};
                  obj.name = "Track";
                  obj.percentage = prov.percentage;
                  obj.color = trackColour;

                  journey.routeProfile.push(obj);
                } else {
                  journey.routeProfile[track].percentage += prov.percentage;
                }

                break;

              // "Other"
              default:
                if (!journey.routeProfile[other]) {
                  const obj = {};
                  obj.name = "Other";
                  obj.percentage = prov.percentage;
                  obj.color = otherColour;

                  journey.routeProfile.push(obj);
                } else {
                  journey.routeProfile[other].percentage += prov.percentage;
                }
            }

            if (dismountPercentage > 0 && !journey.routeProfile[dismounted]) {
              const dismountObj = {
                name: "Dismount",
                percentage: dismountPercentage,
                color: dismountedRoadColour,
              };
              journey.routeProfile.push(dismountObj);
            }

            // Sort the route profile categories by their percentage values
            journey.routeProfile.sort((a, b) => b.percentage - a.percentage);
          });

          /*
					====================================
					* Part 3 - Turn by Turn Navigation *
					====================================
					*/

          // Loop through the turn by turn navigation steps
          journey.marker.forEach((step) => {
            // Assign icons based on step text
            switch (step["@attributes"].turn) {
              case "straight on":
              case "":
                step["@attributes"].turnIcon = "straight_on";
                break;

              case "bear right":
                step["@attributes"].turnIcon = "bear_right";
                break;

              case "turn right":
                step["@attributes"].turnIcon = "turn_right";
                break;

              case "sharp right":
                step["@attributes"].turnIcon = "sharp_right";
                break;

              case "double-back":
                step["@attributes"].turnIcon = "uturn";
                break;

              case "bear left":
                step["@attributes"].turnIcon = "bear_left";
                break;

              case "turn left":
                step["@attributes"].turnIcon = "turn_left";
                break;

              case "sharp left":
                step["@attributes"].turnIcon = "sharp_left";
                break;

              case "join roundabout":
              case "first exit":
              case "second exit":
              case "third exit":
              case "fourth exit":
              case "fifth exit":
              case "sixth exit":
              case "seventh or more exit":
                step["@attributes"].turnIcon = "roundabout";
                break;

              case "unknown":
                step["@attributes"].turnIcon = "map";
                break;

              default:
                step["@attributes"].turnIcon = "map";
            }

            // Assign colours based on provision name (road type)
            switch (step["@attributes"].provisionName) {
              case "Major road":
              case "Main road":
                step["@attributes"].color = majorRoadColour;
                break;

              case "Service road":
              case "Minor road":
              case "Trunk road":
              case "Trunk road access":
              case "Residential street":
              case "Unclassified road":
                step["@attributes"].color = minorRoadColour;
                break;

              case "Cycle path":
              case "Track":
              case "Bridleway":
                step["@attributes"].color = trackColour;
                break;

              case "Footpath":
              case "Pedestrianized area":
              case "Path":
                step["@attributes"].color = pedestrianAreaColour;
                break;

              default:
                step["@attributes"].color = otherColour;
            }

            step["@attributes"].walk === "1"
              ? (step["@attributes"].color = dismountedRoadColour)
              : (step["@attributes"].color = step["@attributes"].color);
          });

          /*
					======================
					* Part 4 - Polylines *
					======================
					*/

          // Create an array to store polyline data to pass to the map
          journey.polylines = [];

          // Get the data we'll need to plot the polyline depending on the currently chosen journey type(see "current" above)
          const lineData = [...journey.marker];

          // Shift it to remove lineData[0], which a summary of the whole journey
          lineData.shift();

          // Loop through each step of the journey and build a coloured polyline for each one
          lineData.forEach((step) => {
            const polyline = {
              color:
                step["@attributes"].walk === "1"
                  ? dismountedRoadColour
                  : step["@attributes"].color,
              path: [],
              opacity: step["@attributes"].walk === "1" ? 0 : 0.8,
            };

            // Get the coordinates from the journey, which the API has returned as a large string
            const { points } = step["@attributes"];

            // Break the space-delimited string down in to an array of "lng,lat" strings
            const coords = points.split(" ");

            // Loop through that array, splitting each item by comma, to get separate lng/lat pairs
            coords.forEach((i) => {
              const point = i.split(",");
              // NB: We correct the order of lat and lng when declaring processedPoint:
              const processedPoint = {
                lat: parseFloat(point[1]),
                lng: parseFloat(point[0]),
              };
              polyline.path.push(processedPoint);
            });

            // Store the route in the routes array
            journey.polylines.push({
              color: polyline.color,
              path: polyline.path,
              opacity: polyline.opacity,
              walk: step["@attributes"].walk === "1" ? true : false,
            });
          });

          /*
					============================
					* Part 5 - Elevation Graph *
					============================
					*/

          journey.elevation_graph = [];

          distances.reduce((sum, val, i, arr) => {
            const runningValue = val + sum;
            const elevation = elevations[i];

            elevationMax = elevation > elevationMax ? elevation : elevationMax;

            const point = {
              distance: (runningValue / 1609.344).toFixed(1),
              // Meters:distance: (runningValue).toFixed(1),
              elevation: elevation,
              location: coordinates[i],
            };

            journey.elevation_graph.push(point);

            return runningValue;
          }, 0);

          // Downsample data
          const sampler = modeMedian();
          sampler.bucketSize(journey.elevation_graph.length / 25);
          journey.elevation_graph = sampler(journey.elevation_graph);

          /*
          ============================
          * Part 6 - GPX and KML download links *
          ============================
          */

          journey.download_files = {};

          const journeyId = journey.marker[0]["@attributes"].itinerary;
          const plan = journey.marker[0]["@attributes"].plan;

          const query = `https://www.cyclestreets.net/journey/${journeyId}/cyclestreets${journeyId}${plan}`;
          // KML
          journey.download_files.kml = query + ".kml";
          // GPX
          journey.download_files.gpx = query + ".gpx";
        });

        // Resolve the promise, passing the returned and processed data to the cycleRoutes reducer to update state
        const alljourneys = {
          fastest: response[0],
          balanced: response[1],
          quietest: response[2],
          elevationMax: elevationMax + 10,
          gotData: true,
        };

        resolve(alljourneys);
      })
      .catch((error) => {
        reject("Could not get journeys");
      });
  });

  promise[CANCEL] = () => {
    source.cancel("Operation cancelled by the user.");
  };

  return promise;
};

// Function to get Places of Interest. For CycleStreets, this is only "bike parking". The rest come from GIS.
export const getParking = (options) => {
  const { locations } = options;
  const radius = 1;
  const limit = 200;

  return new Promise((resolve, reject) => {
    source = CancelToken.source();

    const currentState = store.getState();
    const currentParking =
      currentState.POI.poi["bikeParking"] &&
      currentState.POI.poi["bikeParking"].results;

    // Filter locations that we already have
    let newLocations = locations;
    if (currentParking) {
      newLocations = _.filter(locations, (new_location) => {
        const match = _.find(currentParking, (current_location) => {
          return _.isEqual(current_location.location, new_location)
            ? true
            : false;
        });

        return match ? false : true;
      });
    }

    // query for all locations that we don't already have
    const queries = newLocations.map((location) => {
      const { lat, lng } = location;
      const query = `https://api.cyclestreets.net/v2/pois.locations?key=8db8956117439aa4&type=cycleparking&longitude=${lng}&latitude=${lat}&radius=${radius}&limit=${limit}&fields=id,latitude,longitude,name,osmTags`;

      return callCycleStreets(query, "poi");
    });

    Promise.all(queries).then((response) => {
      const data = response.map((item, i) => {
        return {
          location: locations[i],
          markers: item
            .map((single) => {
              const location = {
                lat: single.geometry.coordinates[1],
                lng: single.geometry.coordinates[0],
              };

              let title = "Bike Parking";
              const capacity = single.properties.osmTags.capacity;

              const content = `
						<h4 class="infowindow-title">${title}</h4>
						<p>Capacity: ${capacity}</p>
						`;

              const icon = {
                url: `${window.ROOT}${window.PORT}/images/markers/parking.svg`,
                size: { width: 35, height: 45 },
                scaledSize: { width: 35, height: 45 },
                anchor: { x: 18, y: 45 },
              };

              const marker = {
                location,
                title,
                content,
                icon,
              };

              return marker;
            })
            .filter((marker) => marker !== null),
        };
      });

      // Combine existing with new
      const combinedData = currentParking ? [...currentParking, ...data] : data;

      resolve(combinedData);
    });
  });
};



// WEBPACK FOOTER //
// ./src/services/cycleStreets.js